import { resolveReadByMeAfterSocketRead } from '../realtime/chatSocketTypes';
import type { ChatListFilter, ChatMessage, ChatThread } from './types';

export type ThreadMessagesState = {
  items: ChatMessage[];
  nextCursor: number | null;
  isLoading: boolean;
  isLoadingOlder: boolean;
  isLoaded: boolean;
};

export type ChatState = {
  filter: ChatListFilter;
  draft: string;
  activeId: number | null;
  threads: ChatThread[];
  isThreadsLoading: boolean;
  messagesByThread: Record<number, ThreadMessagesState>;
};

export type ChatAction =
  | { type: 'filter/set'; payload: ChatListFilter }
  | { type: 'draft/set'; payload: string }
  | { type: 'active/set'; payload: number | null }
  | { type: 'threads/loading'; payload: boolean }
  | { type: 'threads/replace'; payload: ChatThread[] }
  | { type: 'threads/append'; payload: ChatThread[] }
  | { type: 'threads/remove'; payload: number }
  | { type: 'threads/archive'; payload: { chatId: number; isArchived: boolean } }
  | { type: 'threads/setPinnedMessage'; payload: { chatId: number; pinnedMessage: ChatMessage | null } }
  | { type: 'messages/loading'; payload: { chatId: number; value: boolean } }
  | { type: 'messages/loadingOlder'; payload: { chatId: number; value: boolean } }
  | {
      type: 'messages/replace';
      payload: { chatId: number; items: ChatMessage[]; nextCursor: number | null };
    }
  | {
      type: 'messages/prepend';
      payload: { chatId: number; items: ChatMessage[]; nextCursor: number | null };
    }
  | { type: 'messages/upsert'; payload: { chatId: number; items: ChatMessage[] } }
  | { type: 'message/optimisticAdd'; payload: { chatId: number; item: ChatMessage } }
  | { type: 'message/retry'; payload: { chatId: number; clientMessageId: string } }
  | { type: 'message/confirm'; payload: { chatId: number; clientMessageId: string; item: ChatMessage } }
  | { type: 'message/fail'; payload: { chatId: number; clientMessageId: string } }
  | { type: 'messages/markRead'; payload: { chatId: number; messageIds: number[]; unreadCount: number } }
  | { type: 'threads/upsert'; payload: ChatThread }
  | { type: 'messages/remove'; payload: { chatId: number; messageId: number } }
  | {
      type: 'messages/updateReadReceipts';
      payload: {
        chatId: number;
        readerUserId: number;
        currentUserId: number;
        receipts: Array<{ id: number; readByUserIds: number[] }>;
      };
    };

function createThreadMessagesState(): ThreadMessagesState {
  return {
    items: [],
    nextCursor: null,
    isLoading: false,
    isLoadingOlder: false,
    isLoaded: false,
  };
}

function getBucket(state: ChatState, chatId: number): ThreadMessagesState {
  return state.messagesByThread[chatId] ?? createThreadMessagesState();
}

function sortAsc(items: ChatMessage[]): ChatMessage[] {
  return [...items].sort((a, b) => a.sentAt.getTime() - b.sentAt.getTime() || a.id - b.id);
}

function getAttachmentSignature(item: ChatMessage): string {
  return [...item.attachments]
    .map((attachment) => `${attachment.kind}:${attachment.size}`)
    .sort()
    .join('|');
}

function getAttachmentKindSignature(item: ChatMessage): string {
  return [...item.attachments]
    .map((attachment) => attachment.kind)
    .sort()
    .join('|');
}

function mergeResolvedMessage(current: ChatMessage, incoming: ChatMessage): ChatMessage {
  return {
    ...current,
    ...incoming,
    replyTo: incoming.replyTo ?? current.replyTo,
    pending: false,
    failed: false,
    clientMessageId: incoming.clientMessageId ?? current.clientMessageId,
  };
}

function isOptimisticEcho(current: ChatMessage, incoming: ChatMessage): boolean {
  if (current.id >= 0 || !current.isOwn || !incoming.isOwn) return false;
  if (!(current.pending || current.failed)) return false;
  if ((current.text ?? '') !== (incoming.text ?? '')) return false;

  const exactAttachmentMatch = getAttachmentSignature(current) === getAttachmentSignature(incoming);
  const fuzzyAttachmentMatch =
    current.attachments.length > 0 &&
    current.attachments.length === incoming.attachments.length &&
    getAttachmentKindSignature(current) === getAttachmentKindSignature(incoming);

  if (!exactAttachmentMatch && !fuzzyAttachmentMatch) return false;

  return Math.abs(current.sentAt.getTime() - incoming.sentAt.getTime()) <= 120_000;
}

function mergeUnique(current: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  const next = [...current];

  incoming.forEach((item) => {
    const exactIdIndex = next.findIndex((existing) => existing.id === item.id);
    if (exactIdIndex >= 0) {
      next[exactIdIndex] = mergeResolvedMessage(next[exactIdIndex], item);
      return;
    }

    if (item.clientMessageId) {
      const clientMessageIndex = next.findIndex(
        (existing) => existing.clientMessageId === item.clientMessageId,
      );
      if (clientMessageIndex >= 0) {
        next[clientMessageIndex] = mergeResolvedMessage(next[clientMessageIndex], item);
        return;
      }
    }

    const optimisticIndex = next.findIndex((existing) => isOptimisticEcho(existing, item));
    if (optimisticIndex >= 0) {
      next[optimisticIndex] = mergeResolvedMessage(next[optimisticIndex], item);
      return;
    }

    next.push(item);
  });

  return sortAsc(next);
}

export const initialChatState: ChatState = {
  filter: 'all',
  draft: '',
  activeId: null,
  threads: [],
  isThreadsLoading: false,
  messagesByThread: {},
};

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'filter/set':
      return { ...state, filter: action.payload };

    case 'draft/set':
      return { ...state, draft: action.payload };

    case 'active/set':
      return { ...state, activeId: action.payload };

    case 'threads/loading':
      return { ...state, isThreadsLoading: action.payload };

    case 'threads/replace': {
      const nextThreads = action.payload;
      const activeStillExists =
        state.activeId !== null && nextThreads.some((thread) => thread.id === state.activeId);

      return {
        ...state,
        threads: nextThreads,
        activeId: activeStillExists ? state.activeId : (nextThreads[0]?.id ?? null),
      };
    }

    case 'threads/append': {
      const existingIds = new Set(state.threads.map((thread) => thread.id));
      const nextThreads = action.payload.filter((thread) => !existingIds.has(thread.id));

      if (!nextThreads.length) {
        return state;
      }

      return {
        ...state,
        threads: [...state.threads, ...nextThreads],
      };
    }

    case 'threads/remove': {
      const nextThreads = state.threads.filter((thread) => thread.id !== action.payload);
      const activeStillExists =
        state.activeId !== null && nextThreads.some((thread) => thread.id === state.activeId);

      return {
        ...state,
        threads: nextThreads,
        activeId: activeStillExists ? state.activeId : null,
      };
    }

    case 'threads/archive': {
      return {
        ...state,
        threads: state.threads.map((thread) =>
          thread.id === action.payload.chatId
            ? { ...thread, isArchived: action.payload.isArchived }
            : thread,
        ),
      };
    }

    case 'threads/setPinnedMessage': {
      return {
        ...state,
        threads: state.threads.map((thread) =>
          thread.id === action.payload.chatId
            ? { ...thread, pinnedMessage: action.payload.pinnedMessage }
            : thread,
        ),
      };
    }

    case 'messages/loading': {
      const bucket = getBucket(state, action.payload.chatId);

      return {
        ...state,
        messagesByThread: {
          ...state.messagesByThread,
          [action.payload.chatId]: {
            ...bucket,
            isLoading: action.payload.value,
          },
        },
      };
    }

    case 'messages/loadingOlder': {
      const bucket = getBucket(state, action.payload.chatId);

      return {
        ...state,
        messagesByThread: {
          ...state.messagesByThread,
          [action.payload.chatId]: {
            ...bucket,
            isLoadingOlder: action.payload.value,
          },
        },
      };
    }

    case 'messages/replace':
      return {
        ...state,
        messagesByThread: {
          ...state.messagesByThread,
          [action.payload.chatId]: {
            items: sortAsc(action.payload.items),
            nextCursor: action.payload.nextCursor,
            isLoading: false,
            isLoadingOlder: false,
            isLoaded: true,
          },
        },
      };

    case 'messages/prepend': {
      const bucket = getBucket(state, action.payload.chatId);

      return {
        ...state,
        messagesByThread: {
          ...state.messagesByThread,
          [action.payload.chatId]: {
            ...bucket,
            items: mergeUnique(action.payload.items, bucket.items),
            nextCursor: action.payload.nextCursor,
            isLoading: false,
            isLoadingOlder: false,
            isLoaded: true,
          },
        },
      };
    }

    case 'messages/upsert': {
      const bucket = getBucket(state, action.payload.chatId);

      return {
        ...state,
        messagesByThread: {
          ...state.messagesByThread,
          [action.payload.chatId]: {
            ...bucket,
            items: mergeUnique(bucket.items, action.payload.items),
            isLoading: false,
            isLoaded: true,
          },
        },
      };
    }

    case 'message/optimisticAdd': {
      const bucket = getBucket(state, action.payload.chatId);

      return {
        ...state,
        draft: '',
        messagesByThread: {
          ...state.messagesByThread,
          [action.payload.chatId]: {
            ...bucket,
            items: sortAsc([...bucket.items, action.payload.item]),
            isLoaded: true,
          },
        },
      };
    }

    case 'message/confirm': {
      const bucket = getBucket(state, action.payload.chatId);
      const confirmedItem: ChatMessage = {
        ...action.payload.item,
        pending: false,
        failed: false,
        clientMessageId: action.payload.item.clientMessageId ?? action.payload.clientMessageId,
      };
      const optimisticMatch = bucket.items.find(
        (item) => item.clientMessageId === action.payload.clientMessageId,
      );
      const itemsWithoutOptimistic = bucket.items.filter(
        (item) => item.clientMessageId !== action.payload.clientMessageId,
      );
      const mergedItems = optimisticMatch
        ? mergeUnique(
            [...itemsWithoutOptimistic, mergeResolvedMessage(optimisticMatch, confirmedItem)],
            [confirmedItem],
          )
        : mergeUnique(itemsWithoutOptimistic, [confirmedItem]);

      return {
        ...state,
        messagesByThread: {
          ...state.messagesByThread,
          [action.payload.chatId]: {
            ...bucket,
            items: mergedItems,
          },
        },
      };
    }

    case 'message/retry': {
      const bucket = getBucket(state, action.payload.chatId);

      return {
        ...state,
        messagesByThread: {
          ...state.messagesByThread,
          [action.payload.chatId]: {
            ...bucket,
            items: bucket.items.map((item) =>
              item.clientMessageId === action.payload.clientMessageId
                ? { ...item, pending: true, failed: false }
                : item,
            ),
          },
        },
      };
    }

    case 'message/fail': {
      const bucket = getBucket(state, action.payload.chatId);

      return {
        ...state,
        messagesByThread: {
          ...state.messagesByThread,
          [action.payload.chatId]: {
            ...bucket,
            items: bucket.items.map((item) =>
              item.clientMessageId === action.payload.clientMessageId
                ? { ...item, pending: false, failed: true }
                : item,
            ),
          },
        },
      };
    }

    case 'messages/markRead': {
      const bucket = getBucket(state, action.payload.chatId);

      return {
        ...state,
        threads: state.threads.map((thread) =>
          thread.id === action.payload.chatId
            ? { ...thread, unreadCount: action.payload.unreadCount }
            : thread,
        ),
        messagesByThread: {
          ...state.messagesByThread,
          [action.payload.chatId]: {
            ...bucket,
            items: bucket.items.map((item) =>
              action.payload.messageIds.includes(item.id) && !item.isOwn
                ? { ...item, readByMe: true }
                : item,
            ),
          },
        },
      };
    }

    case 'threads/upsert': {
      const index = state.threads.findIndex((thread) => thread.id === action.payload.id);

      if (index >= 0) {
        const merged = { ...state.threads[index], ...action.payload };
        const rest = state.threads.filter((thread) => thread.id !== action.payload.id);

        return {
          ...state,
          threads: [merged, ...rest],
        };
      }

      return {
        ...state,
        threads: [action.payload, ...state.threads],
      };
    }

    case 'messages/remove': {
      const bucket = getBucket(state, action.payload.chatId);

      return {
        ...state,
        messagesByThread: {
          ...state.messagesByThread,
          [action.payload.chatId]: {
            ...bucket,
            items: bucket.items.filter((item) => item.id !== action.payload.messageId),
          },
        },
      };
    }

    case 'messages/updateReadReceipts': {
      const { chatId, readerUserId, currentUserId, receipts } = action.payload;
      const bucket = getBucket(state, chatId);
      const receiptMap = new Map(receipts.map((receipt) => [receipt.id, receipt.readByUserIds]));

      return {
        ...state,
        messagesByThread: {
          ...state.messagesByThread,
          [chatId]: {
            ...bucket,
            items: bucket.items.map((item) => {
              const readByUserIds = receiptMap.get(item.id);
              if (!readByUserIds) {
                return item;
              }

              if (item.isOwn) {
                const readByOthers = readByUserIds.some((userId) => userId !== currentUserId);
                return {
                  ...item,
                  outgoingStatus: readByOthers ? 'read' : item.outgoingStatus,
                };
              }

              const readByMe = resolveReadByMeAfterSocketRead({
                messageSenderId: item.authorId,
                readerUserId,
                messageId: item.id,
                readMessageIds: [item.id],
                myUserId: currentUserId,
              });

              return readByMe ? { ...item, readByMe: true } : item;
            }),
          },
        },
      };
    }

    default:
      return state;
  }
}
